import { GUEST_LOBBIES_ENABLED } from '@/lib/config';
import { cn } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { SiteFooter } from '@/components/layout/SiteFooter';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ModeConfirmModal } from '@/components/shared/ModeConfirmModal';
import { FriendPlayModal } from '@/components/shared/FriendPlayModal';
import { TrainingOfferModal } from '@/features/training/components/TrainingOfferModal';
import { AuctionModeModal } from '@/features/auction/components/AuctionModeModal';
import { RankedModeModal } from '@/features/play/RankedModeModal';
import { FootballGridModeModal } from '@/features/football-grid/components/FootballGridModeModal';
import { CodenamesModeModal } from '@/features/codenames/CodenamesModeModal';
import { setRoomPreset, writeBoard } from '@/features/codenames/codenames.board';
import type { BoardKind } from '@/features/codenames/codenames.view';
import { HomeRecentMatches } from '@/components/shared/HomeRecentMatches';
import { AllGamesGrid } from '@/features/play/AllGamesGrid';
import { PlayAnnouncements } from '@/features/play/PlayAnnouncements';
import { useLocale } from '@/contexts/LocaleContext';
import { storage, STORAGE_KEYS } from '@/utils/storage';
import { useTierLabel } from '@/hooks/useTierLabel';
import { getI18nText } from '@/lib/utils/i18n';
import type { MatchStatsSummary } from '@/lib/domain';
import type { RankedProfileResponse } from '@/lib/repositories/ranked.repo';
import { useObjectives } from '@/lib/queries/objectives.queries';
import { useObjectivesEnabled } from '@/lib/hooks/useObjectivesEnabled';
import { useActiveEventMode } from '@/lib/hooks/useActiveEventMode';

import { colors } from '@/lib/colors';
import { isAuctionCardEnabled, isTicTacToeEnabled } from '@/lib/features/playModes';
import { WeekendLeagueProgressExperimentRail } from '@/features/weekend-league/components/WeekendLeagueProgressExperimentRail';
import { trackWlBannerClicked, trackWlBannerViewed } from '@/lib/analytics/game-events';

import { getNextTierBand } from '@/utils/rankedTier';
import { footballGridAssetUrl } from '@/lib/football-grid/assets';
import { useAuthPromptStore } from '@/stores/authPrompt.store';
import { useDirectFriendRoom } from '@/features/friend/hooks/useDirectFriendRoom';
import { useIsGuest } from '@/lib/auth/useIsGuest';
import { useAuthStore } from '@/stores/auth.store';
import { findPublicGameByModeId, publicGamePath as gamePagePath } from '@/lib/seo/public-games';
import { PracticeDemo } from '@/features/marketing/public/PracticeLayer';
import { WinRateStat } from './WinRateStat';
import { CARD_WIDTH, CardScroller } from './CardScroller';
import { TrabzonsporQuizCard } from '@/features/campaigns/trabzonspor/TrabzonsporQuizCard';

const PLAY_ENTRANCE_SESSION_KEY = 'quizball.playEntranceSeen';
const PLAY_ENTRANCE_INITIAL = { opacity: 0.88, scale: 0.985 } as const;
const PLAY_ENTRANCE_ANIMATE = { opacity: 1, scale: 1 } as const;
// Keyframes from the pre-state: `initial` stays false so the server HTML and the first client render match.
const PLAY_ENTRANCE_KEYFRAMES = { opacity: [PLAY_ENTRANCE_INITIAL.opacity, 1], scale: [PLAY_ENTRANCE_INITIAL.scale, 1] };
const PLAY_ENTRANCE_TRANSITION = { duration: 0.22, ease: 'easeOut' } as const;

function shouldPlayEntranceAnimation() {
  if (typeof window === 'undefined') return false;

  try {
    return window.sessionStorage.getItem(PLAY_ENTRANCE_SESSION_KEY) !== '1';
  } catch {
    return false;
  }
}


function RpProgressBar({ current, target }: { current: number; target: number }) {
  const pct = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;
  return (
    <div
      className="h-3.5 md:h-[18px] w-full overflow-hidden rounded-[5px]"
      style={{ backgroundColor: '#195006' }}
    >
      <div
        className="h-full rounded-[5px] bg-brand-yellow transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/** Compact mode card (bottom row): title + subtitle, big pictogram icon
 *  bottom-right, optional NEW pill. The whole card is the tap target. */
function MiniModeCard({
  bg,
  dark = false,
  title,
  subtitle,
  iconSrc,
  badge,
  badge2 = null,
  ctaLabel,
  className,
  href,
  onClick,
}: {
  bg: string;
  /** true = black text (light card colors) */
  dark?: boolean;
  title: string;
  subtitle: string;
  iconSrc: string;
  badge?: string | null;
  /** Second pill next to the first (e.g. NEW + game count). */
  badge2?: string | null;
  ctaLabel: string;
  className?: string;
  /** Route target — renders a real link (a11y, open-in-new-tab). */
  href?: string;
  /** Modal opener — used when there is no href. */
  onClick?: () => void;
}) {
  const poppins = { fontFamily: "'Poppins', sans-serif", fontWeight: 600 } as const;
  const text = dark ? 'text-black' : 'text-white';
  const cardClassName = cn(
    '@container relative block h-full min-h-[250px] lg:min-h-[300px] cursor-pointer overflow-hidden rounded-[10px] p-3.5 md:p-6 text-left active:translate-y-[2px] transition-all focus-visible:outline-none focus-visible:ring-2',
    className,
  );
  // Phones: pills sit in flow above the title (Georgian titles are too wide to
  // share the line with a floating badge); desktop keeps them floating.
  const inner = (
    <>
      {(badge || badge2) && (
        <div className="z-20 mb-1.5 flex items-center gap-1.5 md:absolute md:top-4 md:right-4 md:mb-0">
          {badge && (
            <div
              className="rounded-full bg-brand-yellow px-2.5 py-1 text-[8px] uppercase tracking-wide text-black md:text-[11px]"
              style={poppins}
            >
              {badge}
            </div>
          )}
          {badge2 && (
            <div
              className={`rounded-full px-2.5 py-1 text-[8px] md:text-[11px] uppercase tracking-wide ${dark ? 'bg-black text-white' : 'bg-white/20 text-white'}`}
              style={poppins}
            >
              {badge2}
            </div>
          )}
        </div>
      )}
      <div className="relative z-10 flex h-full flex-col">
        {/* keep-all: Georgian has no hyphenation — auto-hyphens split words
            mid-syllable with no visible hyphen ("გამოწვევ/ა"). The 11cqi cap
            shrinks a word too long for a narrow card ("Товарищеский" at 360). */}
        <h3
          className={`${badge || badge2 ? 'md:pr-12' : 'pr-1'} text-[min(1rem,11cqi)] md:text-[min(clamp(1.4rem,2.2vw,2rem),11cqi)] leading-[1.12] uppercase [overflow-wrap:normal] [word-break:keep-all] [hyphens:none] ${text}`}
          style={poppins}
        >
          {title}
        </h3>
        <p className={`mt-1.5 text-[10px] uppercase md:text-[14px] ${dark ? 'text-black/70' : 'text-white/80'}`} style={poppins}>
          {subtitle}
        </p>
        <div className="mt-2 flex flex-1 items-center justify-center lg:hidden">
          <Image
            src={iconSrc}
            alt=""
            width={200}
            height={200}
            className="pointer-events-none h-[104px] w-[104px] object-contain opacity-90"
          />
        </div>
        <div
          className="mt-2 flex h-9 w-full items-center justify-center rounded-[8px] bg-black text-[12px] uppercase tracking-wide text-white lg:hidden"
          style={poppins}
        >
          {ctaLabel}
        </div>

        <div className="mt-auto hidden items-end gap-3 pt-6 lg:flex">
          <div
            className="flex h-11 w-[136px] shrink-0 items-center justify-center rounded-[8px] bg-black text-base uppercase tracking-wide text-white"
            style={poppins}
          >
            {ctaLabel}
          </div>
          <div className="flex min-w-0 flex-1 justify-end">
            <Image
              src={iconSrc}
              alt=""
              width={200}
              height={200}
              className="pointer-events-none h-24 w-full max-w-24 object-contain object-right opacity-90"
            />
          </div>
        </div>
      </div>
    </>
  );
  if (href) {
    return (
      <Link href={href} className={cardClassName} style={{ backgroundColor: bg }}>
        {inner}
      </Link>
    );
  }
  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      role="button"
      tabIndex={0}
      className={cardClassName}
      style={{ backgroundColor: bg }}
    >
      {inner}
    </div>
  );
}

/** Auction / Tic-Tac-Toe / Codenames card in the main swipe row: title,
 *  subtitle, pictogram, black PLAY bar. The whole card is the tap target. */
function CompactModeCard({
  bg,
  title,
  subtitle,
  iconSrc,
  ctaLabel,
  badge,
  href,
  onClick,
}: {
  bg: string;
  title: string;
  subtitle: string;
  iconSrc: string;
  ctaLabel: string;
  badge?: string;
  href?: string;
  onClick?: () => void;
}) {
  const poppins = { fontFamily: "'Poppins', sans-serif", fontWeight: 600 } as const;
  const className = cn(
    CARD_WIDTH,
    'relative block cursor-pointer overflow-hidden rounded-[10px] p-3 md:p-6 text-left active:translate-y-[2px] transition-all focus-visible:outline-none focus-visible:ring-2',
  );
  const inner = (
    <>
      {/* Phones centre the title, so there the badge sits on the picture instead (below). */}
      {badge && (
        <span
          className="absolute right-4 top-4 z-20 hidden rotate-[8deg] rounded-md bg-brand-yellow px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-black shadow-[0_3px_10px_rgba(0,0,0,0.35)] md:block"
          style={poppins}
        >
          {badge}
        </span>
      )}
      <Image
        src={iconSrc}
        alt=""
        width={160}
        height={160}
        className="hidden lg:block absolute right-4 bottom-4 h-36 w-36 object-contain opacity-90 pointer-events-none"
      />
      <div className="relative z-10 flex h-full flex-col items-center text-center md:items-start md:text-left">
        <h3
          className="text-[0.95rem] leading-[1.05] uppercase text-white [overflow-wrap:normal] [word-break:keep-all] [hyphens:none] md:text-[clamp(1.5rem,2.4vw,2.25rem)]"
          style={poppins}
        >
          {/* An invisible copy of the badge, floated: the title stays level with the
              other cards and only a word that cannot fit beside the badge drops below it.
              -mr-2: the badge sits 8px into the card padding. */}
          {badge && (
            <span
              aria-hidden
              className="invisible float-right -mr-2 mb-2 ml-2 hidden rounded-md px-2.5 py-1 text-[11px] font-black leading-normal tracking-wide md:block"
            >
              {badge}
            </span>
          )}
          {title}
        </h3>
        <p className="mt-1 text-[10px] md:mt-1.5 md:text-base uppercase text-white" style={poppins}>{subtitle}</p>
        <div className="mt-1.5 flex flex-1 items-center justify-center lg:hidden">
          <div className="relative">
            <Image
              src={iconSrc}
              alt=""
              width={500}
              height={500}
              className="h-[110px] w-[110px] object-contain pointer-events-none"
            />
            {badge && (
              <span
                className="absolute -right-1 -top-1 z-20 rotate-[8deg] rounded-md bg-brand-yellow px-2 py-0.5 text-[9px] font-black uppercase tracking-wide text-black shadow-[0_3px_10px_rgba(0,0,0,0.35)] md:hidden"
                style={poppins}
              >
                {badge}
              </span>
            )}
          </div>
        </div>
        <div className="mt-1.5 flex h-[36px] w-full items-center justify-center rounded-[8px] bg-black text-[12px] uppercase tracking-wide text-white lg:hidden" style={poppins}>
          {ctaLabel}
        </div>
        <div className="mt-auto hidden pt-8 lg:block">
          <div className="flex h-11 w-[136px] shrink-0 items-center justify-center rounded-[8px] bg-black text-base uppercase tracking-wide text-white" style={poppins}>
            {ctaLabel}
          </div>
        </div>
      </div>
    </>
  );
  if (href) {
    return (
      <Link href={href} onClick={onClick} className={className} style={{ backgroundColor: bg }}>
        {inner}
      </Link>
    );
  }
  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      role="button"
      tabIndex={0}
      className={className}
      style={{ backgroundColor: bg }}
    >
      {inner}
    </div>
  );
}

interface ModeSelectionScreenProps {
  onSelectMode: (mode: 'ranked' | 'friendly' | 'solo') => void;
  /** Optional experiment-owned content immediately below the Weekend League rail. */
  playHomeNotice?: React.ReactNode;
  /** Opens the normal confirmation modal immediately for deep-linked flows. */
  initialMode?: 'ranked' | 'friendly' | 'solo';
  /** If provided, called when ranked card is clicked BEFORE the confirm modal opens.
   *  Return `true` to prevent the confirm modal from showing (i.e. the caller handles it). */
  onRankedIntercept?: () => boolean;
  /** New-player training gate: when `shouldOffer` is true, tapping the ranked card
   *  first offers a guided training match. Skip marks it seen and continues into
   *  the normal ranked confirm flow. */
  trainingOffer?: {
    shouldOffer: boolean;
    onPlayTraining: () => void;
    onSkip: () => void;
  };
  /** Auction tutorial gate: offered before the first "Find opponents" in the Auction dialog; "Training" there starts it directly. */
  auctionTraining?: {
    shouldOffer: boolean;
    onPlay: () => void;
    onSkip: () => void;
  };
  /** Tic Tac Toe tutorial gate — same shape, offered before the first "Find opponent" in the Tic Tac Toe dialog. */
  gridTraining?: {
    shouldOffer: boolean;
    onPlay: () => void;
    onSkip: () => void;
  };
  ticketsRemaining?: number;
  matchStatsSummary?: MatchStatsSummary | null;
  rankedProfile: RankedProfileResponse | null;
  rankedProfileLoading?: boolean;
  /** Server-rendered content the public hub places between the cards and the footer. */
  beforeFooter?: React.ReactNode;
}


// Owner call: Recent Matches is hidden on the Play page for now.
const SHOW_RECENT_MATCHES = false;

export function ModeSelectionScreen({
  onSelectMode,
  playHomeNotice,
  initialMode,
  onRankedIntercept,
  trainingOffer,
  auctionTraining,
  gridTraining,
  ticketsRemaining = 0,
  matchStatsSummary = null,
  rankedProfile,
  rankedProfileLoading = false,
  beforeFooter,
}: ModeSelectionScreenProps) {
  const { t, locale } = useLocale();
  const tierLabelOf = useTierLabel();

  // Funnel top: the WL rail is above the fold on every /play visit, so a
  // mount IS an impression. Ref-guarded so dev Strict Mode's double-effect
  // doesn't double-count when a dev build points at real analytics.
  const bannerViewedRef = useRef(false);
  useEffect(() => {
    if (bannerViewedRef.current) return;
    bannerViewedRef.current = true;
    trackWlBannerViewed();
  }, []);
  const { isEventMode } = useActiveEventMode();
  // Ranked and Friendly open the shared confirmation modal. Daily and the
  // optional modes navigate directly or open their own modal.
  const [selectedMode, setSelectedMode] = useState<'ranked' | 'friendly' | 'solo' | null>(
    initialMode ?? null,
  );
  const [auctionModalOpen, setAuctionModalOpen] = useState(false);
  const [auctionOfferOpen, setAuctionOfferOpen] = useState(false);
  // The pack chosen before the Tic Tac Toe offer interrupted "Find opponent" — skipping continues with it.
  const [gridOffer, setGridOffer] = useState<{ pack: string } | null>(null);
  const [gridModalOpen, setGridModalOpen] = useState(false);
  // Seeded false so the server and the first client render agree; the
  // session check runs after hydration and only then triggers the entrance.
  const [playEntranceAnimation, setPlayEntranceAnimation] = useState(false);
  useEffect(() => {
    if (!shouldPlayEntranceAnimation()) return;
    queueMicrotask(() => setPlayEntranceAnimation(true));
  }, []);
  const [trainingOfferOpen, setTrainingOfferOpen] = useState(false);

  // Deep-linked flows (`/play?mode=ranked`) open the ranked confirm modal
  // directly via `initialMode`, bypassing the card click. If the new-player
  // training offer is due, swap the confirm modal for the offer. Render-phase
  // state adjustment (React's recommended pattern) — the condition clears
  // itself immediately, and skipping flips `shouldOffer` off before the
  // confirm modal is restored.
  if (trainingOffer?.shouldOffer && selectedMode === 'ranked') {
    setSelectedMode(null);
    setTrainingOfferOpen(true);
  }

  // Ranked entry point shared by the hero card's click and keyboard handlers:
  // brand-new players get the training offer first; everyone else goes straight
  // to the confirm modal.
  const isPlacementInProgress = rankedProfile ? rankedProfile.placementStatus !== 'placed' : false;
  const placementPlayed = rankedProfile?.placementPlayed ?? 0;
  const placementRequired = Math.max(1, rankedProfile?.placementRequired ?? 3);
  const placementMatchesLeft = Math.max(0, placementRequired - placementPlayed);
  const displayRp = isPlacementInProgress ? 0 : (rankedProfile?.rp ?? 0);
  const rankedWinRate = Math.round(matchStatsSummary?.ranked.winRate ?? 0);
  const rankedGamesPlayed = matchStatsSummary?.ranked.gamesPlayed ?? 0;
  const nextTierBand = getNextTierBand(displayRp);
  const nextTierTargetRp = nextTierBand?.minRp ?? null;
  const router = useRouter();
  // Guest mode: signed-out visitors browse the Play page and try demos, but any
  // action that needs an account opens the sign-in dialog instead.
  const isGuest = useIsGuest();
  // isGuest is also true while auth is still loading; only a settled anonymous
  // visitor is routed to the public pages (a returning member keeps the modal).
  const authStatus = useAuthStore((state) => state.status);
  const settledGuest = isGuest && authStatus === 'anonymous';
  const openAuthPrompt = useAuthPromptStore((state) => state.open);
  // Auction / Tic Tac Toe "Play with friend": straight into a private room in that mode.
  const { startFriendRoom } = useDirectFriendRoom({ onFallback: () => setSelectedMode('friendly') });
  // Guest "vs AI" demo runs the practice engine in a full-screen layer; the
  // /demos gallery is not served on production. Auction / Grid guests go to
  // their public pages.
  const [rankedDemoOpen, setRankedDemoOpen] = useState(false);
  const publicPageFor = (modeId: string) => {
    const game = findPublicGameByModeId(modeId);
    return game?.page ? gamePagePath(game, locale) : undefined;
  };
  // Signed-out visitors go straight to the public Auction / Tic Tac Toe page
  // (owner, 2026-09-16); the mode modal is for members. Ranked is untouched.
  const openAuctionCard = () => {
    if (settledGuest) {
      const href = publicPageFor('auction');
      if (href) { router.push(href); return; }
    }
    setAuctionModalOpen(true);
  };
  const openGridCard = () => {
    if (settledGuest) {
      const href = publicPageFor('grid');
      if (href) { router.push(href); return; }
    }
    setGridModalOpen(true);
  };
  // The hero opens the ranked dialog for everyone (same shape as Auction).
  // Codenames: guests read the public page (it hosts the daily, Words practice and the room link); members get the dialog.
  const [codenamesModalOpen, setCodenamesModalOpen] = useState(false);
  const openCodenamesCard = () => {
    // App routes read the stored language: a player on a localized hub keeps reading in it.
    storage.set(STORAGE_KEYS.LOCALE, locale);
    if (settledGuest) {
      const href = publicPageFor('codenames');
      if (href) { router.push(href); return; }
    }
    setCodenamesModalOpen(true);
  };
  const codenamesRoom = (board: BoardKind) => {
    setRoomPreset(board);
    router.push('/friend/room/new?room=codenames');
  };
  const codenamesDaily = () => {
    writeBoard('pictures');
    router.push('/codenames');
  };
  const [rankedModalOpen, setRankedModalOpen] = useState(false);
  const openRankedFlow = () => setRankedModalOpen(true);
  // "Find opponents": guests sign in; brand-new members get the training offer first.
  const findRankedOpponents = () => {
    setRankedModalOpen(false);
    if (isGuest) { openAuthPrompt(); return; }
    if (onRankedIntercept?.()) return;
    if (trainingOffer?.shouldOffer) {
      setTrainingOfferOpen(true);
      return;
    }
    setSelectedMode('ranked');
  };
  // Auction "Training": guests read the public Auction page (it hosts the same tutorial); members start it in place.
  const startAuctionTraining = () => {
    setAuctionModalOpen(false);
    if (isGuest) {
      const href = publicPageFor('auction');
      if (href) router.push(href);
      return;
    }
    auctionTraining?.onPlay();
  };
  const startGridTraining = () => {
    setGridModalOpen(false);
    if (isGuest) {
      const href = publicPageFor('grid');
      if (href) router.push(href);
      return;
    }
    gridTraining?.onPlay();
  };
  // "Training": guests read the public Ranked page (it hosts the same match); members start it in place.
  const startRankedTraining = () => {
    setRankedModalOpen(false);
    if (isGuest) {
      const href = publicPageFor('ranked');
      if (href) { router.push(href); return; }
      setRankedDemoOpen(true);
      return;
    }
    if (trainingOffer) { trainingOffer.onPlayTraining(); return; }
    setRankedDemoOpen(true);
  };
  const objectivesFeatureEnabled = useObjectivesEnabled();
  const objectivesEnabled = objectivesFeatureEnabled && !isGuest;
  const { data: objectivesData, isLoading: objectivesLoading } = useObjectives({ enabled: objectivesEnabled });
  const rankedTitleStyle = {
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 600,
    letterSpacing: "0",
    lineHeight: 1,
  } as const;
  // Shared Poppins style for body/label/button text (replaces the old
  // font-black/font-bold Duolingo weights). Only Poppins 600 is loaded.
  const poppins = { fontFamily: "'Poppins', sans-serif", fontWeight: 600 } as const;

  useEffect(() => {
    if (!playEntranceAnimation) return;

    try {
      window.sessionStorage.setItem(PLAY_ENTRANCE_SESSION_KEY, '1');
    } catch {
      // Session storage can be unavailable in private or restricted contexts.
    }
  }, [playEntranceAnimation]);

  const handleConfirm = () => {
    if (!selectedMode) return;
    // Keep the modal OPEN: the PLAY button switches to its starting spinner
    // while onSelectMode does its pre-navigation work (ranked refetches the
    // live wallet before router.push) and the game route loads. Closing the
    // modal here left the play screen with zero feedback for that window,
    // which read as "the tap didn't register". The modal unmounts naturally
    // with the page on navigation.
    onSelectMode(selectedMode);
  };

  const previewObjectives = [...(objectivesData?.daily.objectives ?? [])]
    .sort((a, b) => {
      if (a.completed !== b.completed) return a.completed ? 1 : -1;
      const aPct = a.target > 0 ? a.progress / a.target : 0;
      const bPct = b.target > 0 ? b.progress / b.target : 0;
      return bPct - aPct;
    })
    .slice(0, 4);
  const hasPreviewObjectives = previewObjectives.length > 0;

  return (
    <motion.div
      initial={false}
      animate={playEntranceAnimation ? PLAY_ENTRANCE_KEYFRAMES : PLAY_ENTRANCE_ANIMATE}
      transition={playEntranceAnimation ? PLAY_ENTRANCE_TRANSITION : { duration: 0 }}
      className="max-w-5xl mx-auto px-4 py-3 space-y-4 md:py-6 md:space-y-5 font-fun"
    >

      {/* ─── 0. Weekend League — the weekly objective, aligned to the mode cards ─── */}
      {/* Instrumented at the placement, not inside Rail — the dev gallery
          mounts every Rail variant and would fire an impression per skin. */}
      <div onClickCapture={trackWlBannerClicked}>
        <WeekendLeagueProgressExperimentRail />
      </div>

      {playHomeNotice}

      {/* ─── 1. Ranked Hero Card ─── */}
      <div
        onClick={openRankedFlow}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openRankedFlow();
          }
        }}
        role="button"
        tabIndex={0}
        className="relative overflow-hidden rounded-[10px] cursor-pointer focus-visible:outline-none focus-visible:ring-2 active:translate-y-[2px] transition-all"
        style={{ backgroundColor: colors.green.base }}
      >
        {/* Ranked trophy — centred horizontally at ~52.6%, anchored from the top
            at ~15% and sized to ~90% of card height so the wrists run off the
            bottom edge and clip there (matches Figma node 1361:52, which sits
            ~104% down the card). The card's overflow-hidden does the clipping. */}
        <Image
          src="/assets/brand/ranked-hands-trophy.svg"
          alt=""
          width={257}
          height={294}
          className="hidden lg:block absolute left-[52.6%] top-[15%] h-[90%] w-auto -translate-x-1/2 object-contain object-top pointer-events-none"
        />

        <div className="relative z-10 p-4 md:p-7">
          {/* ── Desktop layout ── */}
          <div className="hidden lg:flex items-stretch gap-6">
            {/* Left: Title + Play. Title is capped at the trophy's left edge
                (~40% of the card) so long locales (e.g. Georgian) wrap onto a
                second line instead of running under the absolute trophy. */}
            <div className="flex flex-1 min-w-0 flex-col">
              <h2
                className="max-w-[20rem] text-[clamp(1.75rem,3vw,2.75rem)] uppercase text-white [overflow-wrap:normal] [word-break:keep-all] [hyphens:none]"
                style={{ ...rankedTitleStyle, lineHeight: 1.15 }}
              >
                {isEventMode ? t('play.rankedMatchEvent') : t('play.rankedMatch')}
              </h2>
              <div className="mt-1.5 text-lg uppercase tracking-wide text-white/90" style={poppins}>
                {rankedProfileLoading
                  ? t('play.rankedSubtitle')
                  : isPlacementInProgress
                    ? t('play.rankedPlacement', { played: placementPlayed, required: placementRequired })
                    : t('play.rankedSubtitle')}
              </div>

              {/* Play sits flush with the card's bottom padding, matching the
                  secondary mode cards. */}
              <div className="mt-auto pt-5">
                <div className="flex h-[56px] w-[180px] items-center justify-center rounded-[8px] bg-surface-page text-xl uppercase tracking-wide text-white" style={poppins}>
                  {t('common.play')}
                </div>
              </div>
            </div>

            {/* Right: RP stats */}
            <div className="text-right shrink-0 w-[280px]">
              <div className="inline-flex flex-col items-stretch">
                <div className="text-4xl text-brand-yellow drop-shadow-[0_2px_12px_rgba(255,229,0,0.25)] whitespace-nowrap" style={poppins}>
                  {displayRp}/{nextTierTargetRp ?? 600} RP
                </div>
                <div className="mt-2">
                  <RpProgressBar current={displayRp} target={nextTierTargetRp ?? 600} />
                </div>
              </div>
              {!rankedProfileLoading && (
                <WinRateStat
                  text={t('play.winRateLine', { rate: rankedWinRate, games: rankedGamesPlayed })}
                  className="mt-2 block text-[13px] uppercase leading-snug tracking-wide text-white"
                  style={poppins}
                />
              )}
              <div className="mt-1 text-[17px] uppercase tracking-wide text-white" style={poppins}>
                {isPlacementInProgress
                  ? t(
                      placementMatchesLeft === 1
                        ? 'play.matchesToRankReveal'
                        : 'play.matchesToRankRevealPlural',
                      { count: placementMatchesLeft },
                    )
                  : nextTierBand
                    ? <>{t('play.rpToTier', { rp: Math.max(0, (nextTierTargetRp ?? 0) - displayRp) })}<span className="text-brand-yellow">{tierLabelOf(nextTierBand.tier)}</span></>
                    : t('play.maxRankReached')}
              </div>
              {/* Betsson badge — in flow below the rank text so it can never
                  cover it, whatever height the text block reaches. */}
              {isEventMode && (
                <div
                  className="mt-2 inline-flex flex-col items-start rounded-lg px-3 py-1.5"
                  style={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  <span className="text-[8px] font-bold uppercase tracking-wider text-white/60 leading-none">Powered by</span>
                  <Image src="/assets/betsson/3.png" alt="Betsson Sport" width={96} height={18} className="h-4 w-auto object-contain mt-0.5" />
                </div>
              )}
            </div>
          </div>

          {/* ── Mobile layout ── */}
          <div className="lg:hidden">
            {/* Top row: title (left) | RP block (right) */}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h2
                  className="text-[1.55rem] leading-[1.05] uppercase text-white [overflow-wrap:normal] [word-break:keep-all] [hyphens:none]"
                  style={rankedTitleStyle}
                >
                  {t('play.rankedMatch')}
                </h2>
                <div className="mt-1.5 text-[11px] uppercase tracking-wide text-white/90" style={poppins}>
                  {rankedProfileLoading
                    ? t('play.rankedSubtitle')
                    : isPlacementInProgress
                      ? t('play.rankedPlacement', { played: placementPlayed, required: placementRequired })
                      : t('play.rankedSubtitle')}
                </div>
                {/* World Cup event info — mobile, event only */}
              </div>
              <div className="shrink-0 text-right w-[125px]">
                <div className="text-[1.4rem] leading-none text-brand-yellow drop-shadow-[0_2px_12px_rgba(255,229,0,0.25)]" style={poppins}>
                  {displayRp}/{nextTierTargetRp ?? 600} RP
                </div>
                <div className="mt-2">
                  <RpProgressBar current={displayRp} target={nextTierTargetRp ?? 600} />
                </div>
                <div className="mt-1.5 text-[12px] uppercase leading-snug tracking-wide text-white" style={poppins}>
                  {isPlacementInProgress
                    ? t(
                        placementMatchesLeft === 1
                          ? 'play.matchesLeft'
                          : 'play.matchesLeftPlural',
                        { count: placementMatchesLeft },
                      )
                    : nextTierBand
                      ? <>{t('play.rpToTier', { rp: Math.max(0, (nextTierTargetRp ?? 0) - displayRp) })}<span className="text-brand-yellow">{tierLabelOf(nextTierBand.tier)}</span></>
                      : t('play.maxRankReached')}
                </div>
                {/* Betsson badge — mobile only, below tier label, event only */}
                {isEventMode && (
                  <div
                    className="-mt-0.5 inline-flex flex-col items-end rounded-md px-2 py-1 lg:hidden"
                    style={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.15)' }}
                  >
                    <span className="text-[5px] font-bold uppercase tracking-wider text-white/60 leading-none">Powered by</span>
                    <Image src="/assets/betsson/3.png" alt="Betsson Sport" width={72} height={14} className="h-2.5 w-auto object-contain mt-0.5" />
                  </div>
                )}
              </div>
            </div>

            {/* Bottom row: trophy icon + win rate (left) | PLAY (right) */}
            <div className="mt-3 flex items-end justify-between gap-3">
              <div className="flex flex-col items-start gap-2">
                <Image
                  src="/assets/brand/ranked-hands-trophy.svg"
                  alt=""
                  width={257}
                  height={294}
                  className="h-[176px] w-auto object-contain pointer-events-none"
                />
                {!rankedProfileLoading && (
                  <WinRateStat
                    text={t('play.winRateLine', { rate: rankedWinRate, games: rankedGamesPlayed })}
                    className="block text-[13px] uppercase leading-snug tracking-wide text-white"
                    style={poppins}
                  />
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="mb-1 flex h-[44px] w-[120px] items-center justify-center rounded-[8px] bg-surface-page text-[15px] uppercase tracking-wide text-white" style={poppins}>
                  {t('common.play')}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <PlayAnnouncements />

      {/* ─── 2. Mode Cards — Friendly, Tic-Tac-Toe, Codenames, Auction in the
          same swipe row as the daily games (owner call 2026-10-19). Every other
          game lives in the All Games rows below. */}
      <CardScroller>
        <div className={cn(CARD_WIDTH, 'flex')}>
          <MiniModeCard
            bg={colors.blue.brand}
            title={t('play.friendlyMatch')}
            subtitle={t('play.friendlySubtitle')}
            iconSrc="/assets/friendly_match-icon.webp"
            ctaLabel={t('common.play')}
            onClick={() => (isGuest && !GUEST_LOBBIES_ENABLED ? openAuthPrompt() : setSelectedMode('friendly'))}
            className="w-full"
          />
        </div>
        {isTicTacToeEnabled && (
          <CompactModeCard
            bg={colors.red.mid}
            title={t('play.footballGridTitle')}
            subtitle={t('play.footballGridSubtitle')}
            iconSrc={footballGridAssetUrl('/assets/football-grid/card-icon.png')!}
            ctaLabel={t('common.play')}
            onClick={openGridCard}
          />
        )}
        <CompactModeCard
          bg={colors.green.shadow}
          title={t('play.codenamesTitle')}
          subtitle={t('play.codenamesSubtitle')}
          iconSrc="/assets/codenames-card-icon.webp"
          ctaLabel={t('common.play')}
          badge={t('play.newBadge')}
          onClick={openCodenamesCard}
        />
        {isAuctionCardEnabled && (
          <CompactModeCard
            bg="#6B2FB3"
            title={t('play.auctionTitle')}
            subtitle={t('play.auctionSubtitle')}
            iconSrc="/assets/auction-card-icon.webp"
            ctaLabel={t('common.play')}
            onClick={openAuctionCard}
          />
        )}
      </CardScroller>

      {/* ─── 3. Objectives ─── */}
      {objectivesEnabled && (
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base text-white uppercase" style={poppins}>
            {t('play.objectivesTitle')}
          </h2>
          <Link
            href="/objectives"
            style={poppins}
            className="flex items-center justify-center w-[120px] h-[40px] rounded-xl border-2 border-brand-green-light text-xs text-white uppercase tracking-wide hover:bg-brand-green-light/10 transition-colors"
          >
            {t('common.viewAll')}
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 lg:hidden">
          {objectivesLoading && [0, 1].map((item) => (
            <div
              key={item}
              className="rounded-xl bg-brand-green-deep/70 p-3"
            >
              <div className="mb-2 flex items-center justify-center">
                <div className="size-12 animate-pulse rounded-[10px] bg-white/10" />
              </div>
              <div className="h-3 w-3/4 animate-pulse rounded-full bg-white/12" />
              <div className="mt-2 h-2 w-full animate-pulse rounded-full bg-white/8" />
              <div className="mt-4 h-3 overflow-hidden rounded-full bg-surface-mode-trough">
                <div className="h-full w-1/4 animate-pulse rounded-full bg-brand-green-light/55" />
              </div>
              <div className="mt-2 flex items-center justify-between">
                <div className="h-2 w-8 animate-pulse rounded-full bg-white/12" />
                <div className="h-2 w-14 animate-pulse rounded-full bg-white/12" />
              </div>
            </div>
          ))}
          {!objectivesLoading && !hasPreviewObjectives && (
            <Link
              href="/objectives"
              className="col-span-2 rounded-xl bg-brand-green-deep p-4 transition-all hover:bg-brand-green"
            >
              <div className="mb-2 flex items-center justify-center">
                <Image src="/assets/obj_icon.png" alt="" width={45} height={44} className="size-12 object-contain opacity-90" />
              </div>
              <h4 className="text-center text-[11px] leading-tight text-white uppercase" style={poppins}>{t('play.objectivesUnavailable')}</h4>
              <p className="mt-1 text-center text-[10px] leading-tight text-white/75">{t('play.objectivesUnavailableHint')}</p>
            </Link>
          )}
          {previewObjectives.map((objective) => {
            const progressPercent = objective.target > 0
              ? Math.min(100, Math.round((objective.progress / objective.target) * 100))
              : 0;

            return (
              <Link
                key={objective.id}
                href="/objectives"
                className="rounded-xl bg-brand-green-deep p-3 transition-all hover:bg-brand-green"
              >
                <div className="mb-2 flex items-center justify-center">
                  <Image src="/assets/obj_icon.png" alt="" width={45} height={44} className="size-12 object-contain opacity-90" />
                </div>
                <h4 className="text-[10px] leading-tight text-white uppercase truncate" style={poppins}>{getI18nText(objective.title, locale)}</h4>
                <p className="mt-0.5 line-clamp-2 min-h-[22px] text-[9px] leading-tight text-white/80">{getI18nText(objective.description, locale)}</p>
                <div className="mt-4 h-3 overflow-hidden rounded-full bg-surface-mode-trough">
                  <div className="h-full rounded-full bg-brand-green-light" style={{ width: `${progressPercent}%` }} />
                </div>
                <div className="mt-2 flex items-center justify-between text-[9px] uppercase" style={poppins}>
                  <span className="text-white">{objective.progress}/{objective.target}</span>
                  <span className="text-white/65">{t('play.objectiveRewardCoins', { count: objective.rewardCoins })}</span>
                </div>
              </Link>
            );
          })}
        </div>
        <div className="hidden lg:flex gap-3 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4">
          {objectivesLoading && [0, 1, 2].map((item) => (
            <div
              key={item}
              className="shrink-0 w-[260px] rounded-[10px] bg-surface-mode-card/80 p-4 md:w-[300px]"
            >
              <div className="mb-3 flex items-center gap-3">
                <div className="size-12 shrink-0 animate-pulse rounded-[10px] bg-white/10" />
                <div className="min-w-0 flex-1">
                  <div className="h-3 w-3/4 animate-pulse rounded-full bg-white/12" />
                  <div className="mt-2 h-2 w-full animate-pulse rounded-full bg-white/8" />
                </div>
              </div>
              <div className="mb-2.5 h-3 overflow-hidden rounded-full bg-surface-mode-trough-deep">
                <div className="h-full w-1/4 animate-pulse rounded-full bg-brand-green-light/55" />
              </div>
              <div className="flex items-center justify-between">
                <div className="h-3 w-10 animate-pulse rounded-full bg-white/12" />
                <div className="h-3 w-20 animate-pulse rounded-full bg-white/12" />
              </div>
            </div>
          ))}
          {!objectivesLoading && !hasPreviewObjectives && (
            <Link
              href="/objectives"
              className="shrink-0 w-[260px] rounded-[10px] bg-surface-mode-card p-4 transition-all hover:bg-surface-mode-card-hover md:w-[300px]"
            >
              <div className="mb-3 flex items-center gap-3">
                <Image src="/assets/obj_icon.png" alt="" width={45} height={44} className="size-12 object-contain" />
                <div className="min-w-0 flex-1">
                  <h4 className="truncate text-sm uppercase text-white" style={poppins}>{t('play.objectivesUnavailable')}</h4>
                  <p className="truncate text-[11px] uppercase text-white/60" style={poppins}>{t('play.objectivesUnavailableHint')}</p>
                </div>
              </div>
              <div className="mb-2.5 h-3 overflow-hidden rounded-full bg-surface-mode-trough-deep">
                <div className="h-full w-[8%] rounded-full bg-brand-green-light" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-white" style={poppins}>0/1</span>
                <span className="text-xs uppercase text-white" style={poppins}>{t('play.objectiveRewardCoinsAndXp')}</span>
              </div>
            </Link>
          )}
          {previewObjectives.map((objective) => {
            const progressPercent = objective.target > 0
              ? Math.min(100, Math.round((objective.progress / objective.target) * 100))
              : 0;

            return (
              <Link
                key={objective.id}
                href="/objectives"
                className={cn(
                  "shrink-0 w-[260px] rounded-[10px] bg-surface-mode-card p-4 transition-all hover:bg-surface-mode-card-hover md:w-[300px]",
                  objective.completed && "ring-1 ring-brand-green-light/30"
                )}
              >
                <div className="mb-3 flex items-center gap-3">
                  <Image src="/assets/obj_icon.png" alt="" width={45} height={44} className="size-12 object-contain" />
                  <div className="min-w-0 flex-1">
                    <h4 className="truncate text-sm uppercase text-white" style={poppins}>{getI18nText(objective.title, locale)}</h4>
                    <p className="truncate text-[11px] uppercase text-white/60" style={poppins}>{getI18nText(objective.description, locale)}</p>
                  </div>
                </div>
                <div className="mb-2.5 h-3 overflow-hidden rounded-full bg-surface-mode-trough-deep">
                  <div className="h-full rounded-full bg-brand-green-light" style={{ width: `${progressPercent}%` }} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white" style={poppins}>{objective.progress}/{objective.target}</span>
                  <span className="text-xs uppercase text-white" style={poppins}>{t('play.objectiveRewardCoins', { count: objective.rewardCoins })}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
      )}

      {/* ─── 5. All Games — every mode as an artwork card, 3 per row on
              desktop / 2 on mobile (owner call). Sits above Recent Matches. ─── */}
      <TrabzonsporQuizCard onMoreGames={() => document.getElementById('play-all-games')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />

      <div id="play-all-games" className="mt-6 scroll-mt-4 md:mt-8">
        <AllGamesGrid />
      </div>

      {/* ─── 6. Recent Matches — hidden per owner call; kept mounted behind
              this flag so it can be restored in one line. ─── */}
      {SHOW_RECENT_MATCHES && <HomeRecentMatches collapsedOnly />}

      {/* ─── 6. Modals ─── */}
      <ModeConfirmModal
        mode={selectedMode !== 'friendly' ? selectedMode : null}
        isOpen={!!selectedMode && selectedMode !== 'friendly'}
        onOpenChange={(open) => !open && setSelectedMode(null)}
        onConfirm={handleConfirm}
        ticketsRemaining={ticketsRemaining}
      />
      {/* Friendly opens the dedicated create/join-room modal (prod parity). */}
      <FriendPlayModal
        isOpen={selectedMode === 'friendly'}
        onOpenChange={(open) => !open && setSelectedMode(null)}
      />
      {trainingOffer && (
        <TrainingOfferModal
          isOpen={trainingOfferOpen}
          onOpenChange={setTrainingOfferOpen}
          onPlayTraining={() => {
            setTrainingOfferOpen(false);
            trainingOffer.onPlayTraining();
          }}
          onSkip={() => {
            setTrainingOfferOpen(false);
            trainingOffer.onSkip();
            setSelectedMode('ranked');
          }}
        />
      )}
      <AuctionModeModal
        isOpen={auctionModalOpen}
        onOpenChange={setAuctionModalOpen}
        onCreateRoom={() => {}}
        onFindOnline={() => {
          setAuctionModalOpen(false);
          if (isGuest) {
            openAuthPrompt();
            return;
          }
          if (auctionTraining?.shouldOffer) {
            setAuctionOfferOpen(true);
            return;
          }
          router.push('/auction');
        }}
        onTraining={startAuctionTraining}
        onPlayWithFriend={isGuest && !GUEST_LOBBIES_ENABLED ? undefined : () => { setAuctionModalOpen(false); void startFriendRoom('auction'); }}
      />
      {auctionTraining && (
        <TrainingOfferModal
          game="auction"
          isOpen={auctionOfferOpen}
          onOpenChange={setAuctionOfferOpen}
          onPlayTraining={() => {
            setAuctionOfferOpen(false);
            auctionTraining.onPlay();
          }}
          onSkip={() => {
            setAuctionOfferOpen(false);
            auctionTraining.onSkip();
            router.push('/auction');
          }}
        />
      )}
      <CodenamesModeModal
        isOpen={codenamesModalOpen}
        onOpenChange={setCodenamesModalOpen}
        onPlayWithFriend={(board) => { setCodenamesModalOpen(false); codenamesRoom(board); }}
        onDaily={() => { setCodenamesModalOpen(false); codenamesDaily(); }}
      />
      <FootballGridModeModal
        isOpen={gridModalOpen}
        onOpenChange={setGridModalOpen}
        onFindOnline={(pack) => {
          setGridModalOpen(false);
          if (isGuest) {
            openAuthPrompt();
            return;
          }
          if (gridTraining?.shouldOffer) {
            setGridOffer({ pack });
            return;
          }
          router.push(`/tic-tac-toe?source=matchmaking&pack=${pack}`);
        }}
        onTraining={startGridTraining}
        onPlayWithFriend={isGuest && !GUEST_LOBBIES_ENABLED ? undefined : () => { setGridModalOpen(false); void startFriendRoom('football_grid'); }}
        onSamePhone={() => { setGridModalOpen(false); router.push('/tic-tac-toe/local'); }}
        onDailyGrid={() => { setGridModalOpen(false); router.push('/daily-grid'); }}
      />
      {gridTraining && (
        <TrainingOfferModal
          game="grid"
          isOpen={gridOffer !== null}
          onOpenChange={(open) => { if (!open) setGridOffer(null); }}
          onPlayTraining={() => {
            setGridOffer(null);
            gridTraining.onPlay();
          }}
          onSkip={() => {
            const pack = gridOffer?.pack ?? 'european';
            setGridOffer(null);
            gridTraining.onSkip();
            router.push(`/tic-tac-toe?source=matchmaking&pack=${pack}`);
          }}
        />
      )}
      <RankedModeModal
        isOpen={rankedModalOpen}
        onOpenChange={setRankedModalOpen}
        onFindOpponents={findRankedOpponents}
        onTraining={startRankedTraining}
      />
      {rankedDemoOpen && (
        <PracticeDemo slug="match" title={t('play.guestDemoCta')} locale={locale} backHref="/play" onExit={() => setRankedDemoOpen(false)} />
      )}
      {beforeFooter}
      <SiteFooter />
    </motion.div>
  );
}
